// client/src/coms/components/ServerSidebar.js
import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { comsApiGet, comsApiPost, comsApiDelete, comsApiPatch } from '../comsApi';
import { getParticipantPrefs, setParticipantPrefs } from '../voip/participantPrefs';
import { buildVoiceRoomName } from '../voip/voiceUtils';
import {
  clampMenuPos,
  safeArray,
  safeObj,
  getServerMembers,
  getPresenceByChannel,
  clampInt,
  normalizeDbIdKey,
  toInt,
  extractNumericId,
  avatarUrlForUserId,
  getServerLogoUrl,
  makeAcronym,
  pickServerKey,
  normalizeChannel,
  channelsShallowEqual,
} from './sidebar_parts/sidebarUtils';
import SidebarTopDrawer from './sidebar_parts/SidebarTopDrawer';
import { SidebarHeader } from './sidebar_parts/SidebarHeader';
import { ServerOverviewDialog, ServerSidebarBanner } from './ServerBanner';
import ServerEncryptionBadge from '../e2ee/ServerEncryptionBadge';
import { SidebarChannelSections } from './sidebar_parts/SidebarChannelSections';
import { SidebarFooter } from './sidebar_parts/SidebarFooter';
import { useMyPermissions, ADMIN_PERMISSION_KEYS } from '../usePermissions';
import EditChannelModal from './EditChannelModal';
import { openArea, notify, usePrompt } from '../organisation/orgShared';
import { fetchLayout, saveLayout, stepItem, addCategory, addDivider } from '../organisation/channelLayout';
import { saveBookmark } from '../organisation/bookmarkActions';
import InteractionMenu from '../interactions/InteractionMenu';
import { apiPostJson } from '../organisation/orgApi';
import { ArchiveDialog, AutoArchiveDialog, ConvertDialog, MoveToCategoryDialog, ChannelOrganiserDialog, notifyChannelsChanged } from '../organisation/channelLifecycleUi';

const LAST_COMMS_SERVER_KEY = 'lastCommsServerId';

const WHAT_SHALL_WE_PLAY_PREFIX = '[vox-what-shall-we-play-v1]';

function buildWhatShallWePlaySessionId() {
  return `wsp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function buildWhatShallWePlayMessageBody(payload) {
  return `${WHAT_SHALL_WE_PLAY_PREFIX}${JSON.stringify(payload || {})}`;
}

function normalizeWelcomeChannelId(serverLike) {
  const id =
    serverLike && serverLike.welcome_channel_id != null
      ? serverLike.welcome_channel_id
      : serverLike && serverLike.welcomeChannelId != null
        ? serverLike.welcomeChannelId
        : null;
  return toInt(id, 0) || null;
}

function normalizeWelcomeEnabled(serverLike) {
  const value =
    serverLike && serverLike.welcome_message_enabled != null
      ? serverLike.welcome_message_enabled
      : serverLike && serverLike.welcomeMessageEnabled != null
        ? serverLike.welcomeMessageEnabled
        : 0;
  return toInt(value, 0) === 1 ? 1 : 0;
}

export default function ServerSidebar({
  server,
  servers,
  activeChannelId,
  onSelectChannel,
  onSelectServer,
  onOpenTab,
  onCreateChannel,
  onCreateServer,
  onCreate,
  user,
  apiAvailable,
  onChannelsChanged,
  voiceSession,
  onJoinVoiceChannel,
  onLeaveVoiceChannel,
  onToggleVoiceMute,
  onToggleVoiceDeafen,
  voiceShowVideo,
  onToggleVoiceVideo,
  voiceVideoErr,
  onStartWhatShallWePlay,
  serverUnreadById,
  serverMentionCountById,
  hideServerDrawer = false,
  onOpenServerSettings,
  onServersChanged,
  gamesActive = false,
  onOpenGames,
  activeGame = null,
}) {
  const navigate = useNavigate();

  const createServerFn = typeof onCreateServer === 'function' ? onCreateServer : onCreate;

  const [query, setQuery] = useState('');
  const [overviewOpen, setOverviewOpen] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuX, setMenuX] = useState(0);
  const [menuY, setMenuY] = useState(0);
  const [menuChannel, setMenuChannel] = useState(null);
  const [menuInteractionsOpen, setMenuInteractionsOpen] = useState(false);
  const [menuBusy, setMenuBusy] = useState(false);
  const [menuErr, setMenuErr] = useState('');

  const [memberMenuOpen, setMemberMenuOpen] = useState(false);
  const [memberMenuX, setMemberMenuX] = useState(0);
  const [memberMenuY, setMemberMenuY] = useState(0);
  const [memberMenuMember, setMemberMenuMember] = useState(null);
  const [memberMenuPrefs, setMemberMenuPrefs] = useState({ volume: 100, muted: false });
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [memberBlockBusy, setMemberBlockBusy] = useState(false);
  const [memberBlockMsg, setMemberBlockMsg] = useState('');

  const [remoteChannels, setRemoteChannels] = useState([]);
  const [remoteChannelsLoaded, setRemoteChannelsLoaded] = useState(false);

  const [logoBroken, setLogoBroken] = useState(false);
  const [fetchedLogoUrl, setFetchedLogoUrl] = useState('');

  const [myRoles, setMyRoles] = useState([]);
  const [myRolesLoaded, setMyRolesLoaded] = useState(false);

  const [editChannelOpen, setEditChannelOpen] = useState(false);
  const [editChannelTarget, setEditChannelTarget] = useState(null);

  const [channelReadStateById, setChannelReadStateById] = useState({});

  const [localWelcomeChannelId, setLocalWelcomeChannelId] = useState(() => normalizeWelcomeChannelId(server));
  const [localWelcomeEnabled, setLocalWelcomeEnabled] = useState(() => normalizeWelcomeEnabled(server));

  const menuRef = useRef(null);
  const memberMenuRef = useRef(null);
  const restoreAttemptedRef = useRef(false);

  const serverKey = useMemo(() => pickServerKey(server), [server]);
  // Server settings open for the owner and for anyone holding a management permission.
  const myServerPerms = useMyPermissions(serverKey);

  useEffect(() => {
    setLocalWelcomeChannelId(normalizeWelcomeChannelId(server));
    setLocalWelcomeEnabled(normalizeWelcomeEnabled(server));
  }, [server]);

  const effectiveServer = useMemo(() => {
    return {
      ...(safeObj(server)),
      welcome_channel_id: localWelcomeChannelId,
      welcome_message_enabled: localWelcomeEnabled,
    };
  }, [server, localWelcomeChannelId, localWelcomeEnabled]);

  const blockedUserIdSet = useMemo(() => {
    const set = new Set();
    safeArray(blockedUsers).forEach((item) => {
      const uid = toInt(item && item.blocked_user_id, 0);
      if (uid > 0) set.add(uid);
    });
    return set;
  }, [blockedUsers]);

  const memberMenuUserId = useMemo(() => {
    return extractNumericId(memberMenuMember && memberMenuMember.id);
  }, [memberMenuMember]);

  const memberMenuIsBlocked = useMemo(() => {
    return memberMenuUserId > 0 ? blockedUserIdSet.has(memberMenuUserId) : false;
  }, [blockedUserIdSet, memberMenuUserId]);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setMenuChannel(null);
    setMenuBusy(false);
    setMenuErr('');
  }, []);

  const closeMemberMenu = useCallback(() => {
    setMemberMenuOpen(false);
    setMemberMenuMember(null);
    setMemberBlockBusy(false);
    setMemberBlockMsg('');
  }, []);

  const closeEditChannel = useCallback(() => {
    setEditChannelOpen(false);
    setEditChannelTarget(null);
  }, []);

  const refreshBlockedUsers = useCallback(async () => {
    try {
      const res = await comsApiGet('/blocks').catch(() => null);
      setBlockedUsers(safeArray(res && res.blocks));
    } catch {
      setBlockedUsers([]);
    }
  }, []);

  const refreshServerWelcomeState = useCallback(async () => {
    const sid = String(serverKey || '').trim();
    if (!sid) return;

    try {
      const res = await comsApiGet(`/servers/${encodeURIComponent(sid)}`).catch(() => null);
      const nextServer = safeObj(res && res.server);

      setLocalWelcomeChannelId(normalizeWelcomeChannelId(nextServer));
      setLocalWelcomeEnabled(normalizeWelcomeEnabled(nextServer));
    } catch {}
  }, [serverKey]);

  useEffect(() => {
    setRemoteChannels([]);
    setRemoteChannelsLoaded(false);
  }, [serverKey]);

  useEffect(() => {
    setMyRoles([]);
    setMyRolesLoaded(false);
  }, [serverKey]);

  useEffect(() => {
    setLogoBroken(false);
    setFetchedLogoUrl('');
  }, [
    serverKey,
    server && server.logo_url,
    server && server.logoUrl,
    server && server.server_logo_url,
    server && server.serverLogoUrl,
  ]);

  useEffect(() => {
    setChannelReadStateById({});
  }, [serverKey]);

  useEffect(() => {
    const current = String(serverKey || '').trim();
    if (!current) return;
    try {
      localStorage.setItem(LAST_COMMS_SERVER_KEY, current);
    } catch {}
  }, [serverKey]);

  useEffect(() => {
    if (restoreAttemptedRef.current) return;

    const current = String(serverKey || '').trim();
    if (current) {
      restoreAttemptedRef.current = true;
      return;
    }

    const list = safeArray(servers);
    if (!list.length) return;

    restoreAttemptedRef.current = true;

    let saved = '';
    try {
      saved = String(localStorage.getItem(LAST_COMMS_SERVER_KEY) || '').trim();
    } catch {
      saved = '';
    }

    const existingSaved = saved
      ? list.find((s) => String(pickServerKey(s) || '').trim() === saved) || null
      : null;

    const fallback = list[0] || null;
    const target = existingSaved || fallback;
    const nextId = String(pickServerKey(target) || '').trim();

    if (!nextId) return;

    if (typeof onSelectServer === 'function') {
      try {
        onSelectServer(nextId);
        return;
      } catch {}
    }

    navigate(`/comms/${encodeURIComponent(nextId)}`);
  }, [serverKey, servers, onSelectServer, navigate]);

  useEffect(() => {
    let alive = true;

    async function loadLogoFallback() {
      const sid = serverKey ? String(serverKey).trim() : '';
      if (!sid) return;

      const direct = getServerLogoUrl(server);
      if (direct) return;

      try {
        const r = await comsApiGet(`/server-logo/${encodeURIComponent(sid)}`);
        if (!alive) return;
        const logo = r && r.logo_url ? String(r.logo_url).trim() : '';
        setFetchedLogoUrl(logo);
      } catch {
        if (!alive) return;
        setFetchedLogoUrl('');
      }
    }

    loadLogoFallback().catch(() => {});
    return () => {
      alive = false;
    };
  }, [serverKey, server]);

  useEffect(() => {
    refreshBlockedUsers().catch(() => {});
  }, [refreshBlockedUsers, serverKey]);

  useEffect(() => {
    refreshServerWelcomeState().catch(() => {});
  }, [refreshServerWelcomeState, serverKey]);

  useEffect(() => {
    let alive = true;

    async function loadMyRoles() {
      const sid = String(serverKey || '').trim();
      const uid =
        user && typeof user === 'object'
          ? toInt(user.id != null ? user.id : user.user_id != null ? user.user_id : 0, 0)
          : 0;

      if (!sid || !uid) {
        if (alive) {
          setMyRoles([]);
          setMyRolesLoaded(true);
        }
        return;
      }

      try {
        const res = await comsApiGet(
          `/servers/${encodeURIComponent(sid)}/members/${encodeURIComponent(String(uid))}/roles`
        ).catch(() => null);

        if (!alive) return;

        const roles = safeArray(res && res.roles);
        setMyRoles(roles);
        setMyRolesLoaded(true);
      } catch {
        if (!alive) return;
        setMyRoles([]);
        setMyRolesLoaded(true);
      }
    }

    loadMyRoles().catch(() => {});
    return () => {
      alive = false;
    };
  }, [serverKey, user]);

  const serverLogoUrl = useMemo(() => {
    const direct = getServerLogoUrl(server);
    return String(direct || fetchedLogoUrl || '').trim();
  }, [server, fetchedLogoUrl]);

  const hasServerLogo = !!serverLogoUrl && !logoBroken;
  const serverAcronym = useMemo(() => makeAcronym(server && server.name), [server]);

  useEffect(() => {
    let alive = true;
    let t = null;

    async function refreshChannels() {
      if (!serverKey) {
        if (alive) {
          setRemoteChannels([]);
          setRemoteChannelsLoaded(false);
        }
        return;
      }

      try {
        const all = await comsApiGet('/servers').catch(() => null);
        if (!alive) return;

        if (all && all.ok && Array.isArray(all.servers)) {
          const found = all.servers.find((s) => pickServerKey(s) === serverKey);
          const nextChannels = safeArray(found && found.channels).map(normalizeChannel);

          if (found) {
            setLocalWelcomeChannelId(normalizeWelcomeChannelId(found));
            setLocalWelcomeEnabled(normalizeWelcomeEnabled(found));
          }

          setRemoteChannels((prev) => (channelsShallowEqual(prev, nextChannels) ? prev : nextChannels));
          setRemoteChannelsLoaded(true);
          return;
        }

        setRemoteChannels([]);
        setRemoteChannelsLoaded(true);
      } catch {
        if (alive) {
          setRemoteChannels([]);
          setRemoteChannelsLoaded(true);
        }
      }
    }

    refreshChannels();
    // Channel lists rarely change; refresh occasionally and when the window regains focus.
    t = setInterval(refreshChannels, 10000);
    const onFocus = () => refreshChannels();
    window.addEventListener('focus', onFocus);
    window.addEventListener('vox-channels-changed', onFocus);
    // Open chat streams relay "permissions_changed" after any server change (archive, reorder, roles):
    // the channel list follows in real time.
    window.addEventListener('vox-permissions-changed', onFocus);

    return () => {
      alive = false;
      if (t) clearInterval(t);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('vox-channels-changed', onFocus);
      window.removeEventListener('vox-permissions-changed', onFocus);
    };
  }, [serverKey]);

  const localChannels = useMemo(
    () => safeArray(server && server.channels).map(normalizeChannel),
    [server]
  );

  const effectiveChannels = useMemo(() => {
    const local = safeArray(localChannels).map(normalizeChannel);
    const remote = safeArray(remoteChannels).map(normalizeChannel);

    if (!remoteChannelsLoaded || remote.length === 0) return local;

    // Text and voice channels come from separate tables, so a db id is only unique per kind.
    const kindOf = (c) => String((c && c.kind) || 'text');
    const remoteByKey = new Map();

    for (const rc of remote) {
      const rDb = String(rc && rc.db_id != null ? rc.db_id : '').trim();
      const rId = String(rc && rc.id != null ? rc.id : '').trim();

      if (rDb) remoteByKey.set(`${kindOf(rc)}:db:${rDb}`, rc);
      if (rId) remoteByKey.set(`${kindOf(rc)}:id:${rId}`, rc);
    }

    const usedRemote = new Set();
    const merged = local.map((lc) => {
      const lDb = String(lc && lc.db_id != null ? lc.db_id : '').trim();
      const lId = String(lc && lc.id != null ? lc.id : '').trim();

      let rc = null;

      const lk = kindOf(lc);
      if (lDb && remoteByKey.has(`${lk}:db:${lDb}`)) {
        rc = remoteByKey.get(`${lk}:db:${lDb}`);
      } else if (lId && remoteByKey.has(`${lk}:id:${lId}`)) {
        rc = remoteByKey.get(`${lk}:id:${lId}`);
      }

      if (!rc) return lc;

      const remoteIdentityKey = `${kindOf(rc)}:${String(rc.db_id || rc.id || '')}`;
      if (remoteIdentityKey) usedRemote.add(remoteIdentityKey);

      return {
        ...lc,
        ...rc,
        id: lc.id,
        db_id: lc.db_id != null ? lc.db_id : rc.db_id,
      };
    });

    for (const rc of remote) {
      const remoteIdentityKey = `${kindOf(rc)}:${String(rc.db_id || rc.id || '')}`;
      if (remoteIdentityKey && usedRemote.has(remoteIdentityKey)) continue;
      merged.push(rc);
    }

    return merged;
  }, [localChannels, remoteChannels, remoteChannelsLoaded]);

  useEffect(() => {
    const active = String(activeChannelId || '').trim();
    if (!active) return;

    const stillExists = safeArray(effectiveChannels).some((c) => String(c && c.id != null ? c.id : '') === active);
    if (stillExists) return;

    const nextText = safeArray(effectiveChannels).find((c) => String(c && c.kind || '') === 'text');
    const nextVoice = safeArray(effectiveChannels).find((c) => String(c && c.kind || '') === 'voice');
    const fallback = nextText || nextVoice || null;

    if (fallback && typeof onSelectChannel === 'function') {
      onSelectChannel(fallback.id);
      return;
    }

    if (typeof onSelectChannel === 'function') {
      onSelectChannel('');
    }
  }, [activeChannelId, effectiveChannels, onSelectChannel]);

  const filtered = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    const list = effectiveChannels;
    if (!q) return list;
    return list.filter((c) => String((c && c.name) || '').toLowerCase().includes(q));
  }, [query, effectiveChannels]);

  const textChannels = useMemo(() => {
    const welcomeDbId = toInt(localWelcomeChannelId, 0) || null;

    return filtered
      .filter((c) => c.kind === 'text')
      .map((c) => {
        const dbId = toInt(c && c.db_id != null ? c.db_id : c && c.id, 0) || null;
        const isWelcome = !!(welcomeDbId && dbId && welcomeDbId === dbId);

        return {
          ...c,
          is_welcome_channel: isWelcome ? 1 : 0,
          isWelcomeChannel: isWelcome ? 1 : 0,
        };
      });
  }, [filtered, localWelcomeChannelId]);

  const voiceChannels = useMemo(() => filtered.filter((c) => c.kind === 'voice'), [filtered]);
  // Archived text channels leave the normal list; they stay reachable (read-only) in the Archived section.
  const activeTextChannels = useMemo(() => textChannels.filter((c) => !c.archived_at), [textChannels]);
  const archivedTextChannels = useMemo(() => textChannels.filter((c) => !!c.archived_at), [textChannels]);

  // The server's channel order (organisation/channelLayout.js): channels, categories and dividers in one list.
  // Refreshed with the channel list: on changes here, when open chat streams relay a server change
  // ("permissions_changed", sent after every layout change), on focus and every 15 s.
  const [channelLayout, setChannelLayout] = useState(null);
  const layoutSeq = useRef(0);
  const reloadLayout = useCallback(() => {
    if (!serverKey) {
      setChannelLayout(null);
      return;
    }
    const seq = ++layoutSeq.current;
    fetchLayout(serverKey).then(
      (r) => seq === layoutSeq.current && setChannelLayout(Array.isArray(r && r.items) ? r.items : []),
      () => {}
    );
  }, [serverKey]);
  useEffect(() => {
    setChannelLayout(null);
    reloadLayout();
    const t = setInterval(reloadLayout, 15000);
    window.addEventListener('vox-channels-changed', reloadLayout);
    window.addEventListener('vox-permissions-changed', reloadLayout);
    window.addEventListener('focus', reloadLayout);
    return () => {
      layoutSeq.current += 1;
      clearInterval(t);
      window.removeEventListener('vox-channels-changed', reloadLayout);
      window.removeEventListener('vox-permissions-changed', reloadLayout);
      window.removeEventListener('focus', reloadLayout);
    };
  }, [reloadLayout]);
  const categories = useMemo(() => (channelLayout || []).filter((it) => it.type === 'category').map((c) => ({ id: c.id, name: c.name })), [channelLayout]);
  const [layoutPrompt, layoutPromptEl] = usePrompt();
  const [orgDialog, setOrgDialog] = useState(null);

  const textChannelsKey = useMemo(
    () =>
      textChannels
        .map((c) => `${String(c && c.id != null ? c.id : '')}:${String(c && c.db_id != null ? c.db_id : '')}`)
        .join('|'),
    [textChannels]
  );

  const serverMembers = useMemo(() => getServerMembers(server), [server]);
  const presenceByChannel = useMemo(() => getPresenceByChannel(server, voiceSession), [server, voiceSession]);

  const isMobileViewport = useMemo(() => {
    try {
      if (typeof window === 'undefined' || !window.matchMedia) return false;
      return !!window.matchMedia('(max-width: 900px)').matches;
    } catch {
      return false;
    }
  }, []);

  const sidebarRootStyle = isMobileViewport
    ? {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }
    : undefined;

  const sidebarScrollStyle = isMobileViewport
    ? {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
      }
    : undefined;

  const bottomBarWrapStyle = isMobileViewport
    ? {
        marginTop: 'auto',
        flexShrink: 0,
        paddingBottom: 'max(0px, env(safe-area-inset-bottom, 0px))',
      }
    : undefined;

  const memberByUid = useMemo(() => {
    const map = new Map();
    for (const m of safeArray(serverMembers)) {
      const uid = extractNumericId(m && m.id);
      if (!uid) continue;
      const dn = String(m.display_name || '').trim();
      const un = String(m.username || '').trim();
      const nm = String(m.name || '').trim();
      const best = dn || un || nm;
      if (best) map.set(uid, best);
    }
    return map;
  }, [serverMembers]);

  const serverIdForPrefs = useMemo(() => {
    const s = safeObj(server);
    const id = s.id != null ? String(s.id) : s.server_id != null ? String(s.server_id) : '';
    return String(id || '').trim();
  }, [server]);

  const myUserId = useMemo(() => {
    const u = user && typeof user === 'object' ? user : null;
    const id = u && (u.id != null ? u.id : u.user_id != null ? u.user_id : null);
    return toInt(id, 0);
  }, [user]);

  const myMemberName = useMemo(() => {
    if (!myUserId) return '';
    const dn = memberByUid.get(myUserId);
    return dn ? String(dn).trim() : '';
  }, [memberByUid, myUserId]);

  const displayName = useMemo(() => {
    if (myMemberName) return myMemberName;
    if (!user) return 'Guest';
    const v = user.display_name || user.displayName || user.username || user.name || user.email || 'User';
    return String(v || '').trim() || 'User';
  }, [user, myMemberName]);

  const myAvatarUrl = useMemo(() => avatarUrlForUserId(myUserId), [myUserId]);

  const ownerUserId = useMemo(() => {
    const s = safeObj(server);
    const id =
      s.owner_user_id != null
        ? s.owner_user_id
        : s.ownerId != null
          ? s.ownerId
          : s.owner_userId != null
            ? s.owner_userId
            : null;
    return toInt(id, 0);
  }, [server]);

  const isOwner = useMemo(() => {
    if (!myUserId || !ownerUserId) return false;
    return myUserId === ownerUserId;
  }, [myUserId, ownerUserId]);

  const myPermissionsMap = useMemo(() => {
    const out = {};
    for (const role of safeArray(myRoles)) {
      const roleObj = safeObj(role);
      const map = safeObj(roleObj.permissions_map);

      for (const key of Object.keys(map)) {
        if (toInt(map[key], 0) === 1) out[key] = 1;
      }

      const perms = safeArray(roleObj.permissions);
      for (const perm of perms) {
        const key = String(perm && perm.key ? perm.key : '').trim();
        if (!key) continue;
        if (toInt(perm && perm.allow, 0) === 1) out[key] = 1;
      }
    }
    return out;
  }, [myRoles]);

  const canManageChannels = useMemo(() => {
    if (isOwner) return true;
    if (!myRolesLoaded) return false;
    return toInt(myPermissionsMap.manage_channels, 0) === 1;
  }, [isOwner, myPermissionsMap, myRolesLoaded]);

  function channelDbIdFromAny(c) {
    if (!c) return null;
    if (c.db_id != null) return String(c.db_id);
    const raw = c.id != null ? String(c.id) : '';
    const m = raw.match(/(\d+)$/);
    return m && m[1] ? m[1] : null;
  }

  // One batched request for every channel.
  const refreshChannelReadStates = useCallback(async () => {
    if (!serverKey) return;
    let states = {};
    try {
      const res = await comsApiGet(`/servers/${encodeURIComponent(serverKey)}/channel-read-states`);
      states = safeObj(res && res.states);
    } catch {
      return;
    }
    const next = {};
    for (const c of safeArray(textChannels)) {
      const id = String(c && c.id != null ? c.id : '').trim();
      const dbId = String(channelDbIdFromAny(c) || '').trim();
      if (!id || !dbId) continue;
      const state = safeObj(states[dbId]);
      next[id] = {
        unread_count: Math.max(0, toInt(state.unread_count, 0)),
        mention_count: Math.max(0, toInt(state.mention_count, 0)),
        last_seen_message_id: Math.max(0, toInt(state.last_seen_message_id, 0)),
        show_notifications: toInt(state.show_notifications, 1) === 1 ? 1 : 0,
      };
    }
    setChannelReadStateById(next);
  }, [serverKey, textChannels]);

  useEffect(() => {
    let alive = true;
    let t = null;

    async function run() {
      try {
        await refreshChannelReadStates();
      } catch {}
    }

    if (serverKey && textChannels.length) {
      run();
      // Every 4 s while visible, every 16 s in background tabs.
      let lastRun = Date.now();
      t = setInterval(() => {
        if (!alive) return;
        const hidden = typeof document !== 'undefined' && document.hidden;
        if (hidden && Date.now() - lastRun < 16000) return;
        lastRun = Date.now();
        run();
      }, 4000);
    } else {
      setChannelReadStateById({});
    }

    return () => {
      alive = false;
      if (t) clearInterval(t);
    };
  }, [serverKey, textChannelsKey, refreshChannelReadStates, textChannels.length]);

  const markChannelRead = useCallback(
    async (channelId) => {
      const activeId = String(channelId || '').trim();
      if (!activeId) return;

      const channelObj = safeArray(textChannels).find((c) => String(c && c.id != null ? c.id : '').trim() === activeId);
      const dbId = String(channelDbIdFromAny(channelObj) || '').trim();
      if (!dbId) return;

      setChannelReadStateById((prev) => ({
        ...prev,
        [activeId]: {
          ...safeObj(prev && prev[activeId]),
          unread_count: 0,
          mention_count: 0,
        },
      }));

      try {
        const res = await comsApiPost(`/text-channels/${encodeURIComponent(dbId)}/read`, {});
        const lastSeenMessageId = Math.max(0, toInt(res && res.last_seen_message_id, 0));
        setChannelReadStateById((prev) => ({
          ...prev,
          [activeId]: {
            ...safeObj(prev && prev[activeId]),
            unread_count: 0,
            mention_count: 0,
            last_seen_message_id: lastSeenMessageId,
          },
        }));
      } catch {}
    },
    [textChannels]
  );


  useEffect(() => {
    if (!menuOpen && !memberMenuOpen) return;

    function isInside(elRef, target) {
      const el = elRef.current;
      if (!el) return false;
      try {
        return !!(target && el.contains(target));
      } catch {
        return false;
      }
    }

    function onDown(e) {
      if (menuOpen && isInside(menuRef, e && e.target)) return;
      if (memberMenuOpen && isInside(memberMenuRef, e && e.target)) return;
      if (menuOpen) closeMenu();
      if (memberMenuOpen) closeMemberMenu();
    }

    function onContext(e) {
      if (menuOpen && isInside(menuRef, e && e.target)) return;
      if (memberMenuOpen && isInside(memberMenuRef, e && e.target)) return;
      if (menuOpen) closeMenu();
      if (memberMenuOpen) closeMemberMenu();
    }

    function onKey(e) {
      if (e && e.key === 'Escape') {
        if (menuOpen) closeMenu();
        if (memberMenuOpen) closeMemberMenu();
      }
    }

    function onScroll() {
      if (menuOpen) closeMenu();
      if (memberMenuOpen) closeMemberMenu();
    }

    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('contextmenu', onContext, true);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, true);

    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('contextmenu', onContext, true);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [menuOpen, memberMenuOpen, closeMenu, closeMemberMenu]);

  function participantIsSpeaking(p) {
    if (!p || typeof p !== 'object') return false;
    if (p.isSpeaking != null) return !!p.isSpeaking;
    if (p.is_speaking != null) return !!p.is_speaking;
    if (p.speaking != null) return !!p.speaking;

    const levelRaw =
      p.audioLevel != null
        ? p.audioLevel
        : p.audio_level != null
          ? p.audio_level
          : p.level != null
            ? p.level
            : 0;

    const level = Number(levelRaw);
    return Number.isFinite(level) && level > 0.02;
  }

  const onChannelContextMenu = useCallback(
    (e, c) => {
      // Everyone can bookmark a channel, so the menu always has something to offer.

      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();

      closeMemberMenu();

      setMenuErr('');
      setMenuBusy(false);
      setMenuChannel(c);
      setMenuInteractionsOpen(false);
      setMenuOpen(true);

      const x = (e && Number.isFinite(e.clientX) ? e.clientX : 0) || 0;
      const y = (e && Number.isFinite(e.clientY) ? e.clientY : 0) || 0;
      setMenuX(x);
      setMenuY(y);
    },
    [closeMemberMenu]
  );

  const selectedMenuChannelDbId = useMemo(() => {
    return menuChannel ? String(channelDbIdFromAny(menuChannel) || '').trim() : '';
  }, [menuChannel]);

  const menuChannelState = useMemo(() => {
    if (!menuChannel) return null;
    const id = String(menuChannel.id || '').trim();
    return id && channelReadStateById && channelReadStateById[id] ? safeObj(channelReadStateById[id]) : null;
  }, [menuChannel, channelReadStateById]);

  const menuChannelNotificationsEnabled = useMemo(() => {
    return toInt(menuChannelState && menuChannelState.show_notifications, 1) === 1;
  }, [menuChannelState]);

  const menuChannelIsText = useMemo(() => {
    return String((menuChannel && menuChannel.kind) || '').trim().toLowerCase() === 'text';
  }, [menuChannel]);

  const menuChannelIsVoice = useMemo(() => {
    return String((menuChannel && menuChannel.kind) || '').trim().toLowerCase() === 'voice';
  }, [menuChannel]);

  const menuCanToggleNotifications = menuChannelIsText && !!selectedMenuChannelDbId;
  const menuCanManageThisChannel = !!menuChannel && canManageChannels;
  const menuCanStartWhatShallWePlay = !!menuChannel && menuChannelIsVoice;

  const menuChannelArchived = !!(menuChannel && menuChannel.archived_at);
  const menuSize = useMemo(() => {
    const items =
      2 +
      (menuCanToggleNotifications ? 1 : 0) +
      (menuCanStartWhatShallWePlay ? 1 : 0) +
      (menuCanManageThisChannel ? 2 : 0) +
      (menuCanManageThisChannel ? (menuChannelIsText ? 6 : 4) : 0);
    const visibleItems = (items > 0 ? items : 1) + 1 + (menuInteractionsOpen ? 9 : 0);
    return { w: menuInteractionsOpen ? 270 : 240, h: 54 + visibleItems * 40 };
  }, [menuCanToggleNotifications, menuCanStartWhatShallWePlay, menuCanManageThisChannel, menuChannelIsText, menuInteractionsOpen]);

  async function restoreMenuChannel() {
    const c = menuChannel;
    closeMenu();
    try {
      await apiPostJson(`/servers/${encodeURIComponent(serverKey)}/channels/${channelDbIdFromAny(c)}/restore`, {});
      notifyChannelsChanged();
    } catch (e) {
      setMenuErr(String((e && e.message) || 'Restore failed'));
    }
  }

  function openOrgDialog(kind) {
    const c = menuChannel;
    closeMenu();
    setOrgDialog({ kind, channel: { ...c, db_id: channelDbIdFromAny(c) } });
  }

  // Drag-and-drop / keyboard / menu result: shown at once, saved in one request; the server's answer (or a
  // reload after an error) replaces it. Other members get it through their chat streams.
  const onSaveLayout = useCallback(
    async (tree) => {
      if (!serverKey || !tree) return;
      layoutSeq.current += 1;
      setChannelLayout(tree);
      try {
        const r = await saveLayout(serverKey, tree);
        if (r && Array.isArray(r.items)) setChannelLayout(r.items);
        window.dispatchEvent(new CustomEvent('vox-layout-saved', { detail: { serverKey } }));
      } catch (e) {
        notify((e && e.message) || 'Could not save the channel order', { tone: 'error' });
        reloadLayout();
      }
    },
    [serverKey, reloadLayout]
  );

  const onAddLayoutItem = useCallback(
    async (kind) => {
      if (!serverKey) return;
      const isCat = kind === 'category';
      const name = await layoutPrompt(
        isCat
          ? { title: 'Add category', label: 'Category name', maxLength: 64, confirmLabel: 'Add' }
          : { title: 'Add divider', label: 'Label (optional — leave empty for a plain line)', maxLength: 64, confirmLabel: 'Add' }
      );
      if (name == null || (isCat && !String(name).trim())) return;
      try {
        if (isCat) await addCategory(serverKey, String(name).trim());
        else await addDivider(serverKey, String(name).trim());
        notifyChannelsChanged();
      } catch (e) {
        notify((e && e.message) || 'Could not add it', { tone: 'error' });
      }
    },
    [serverKey, layoutPrompt]
  );

  function moveMenuChannel(delta) {
    const c = menuChannel;
    closeMenu();
    const key = `${String((c && c.kind) || 'text') === 'voice' ? 'voice' : 'text'}:${channelDbIdFromAny(c)}`;
    const next = channelLayout ? stepItem(channelLayout, key, delta) : null;
    if (next) onSaveLayout(next);
  }

  const menuPos = useMemo(() => clampMenuPos(menuX, menuY, menuSize.w, menuSize.h), [menuX, menuY, menuSize]);

  const memberMenuSize = useMemo(() => ({ w: 250, h: 214 }), []);
  const memberMenuPos = useMemo(
    () => clampMenuPos(memberMenuX, memberMenuY, memberMenuSize.w, memberMenuSize.h),
    [memberMenuX, memberMenuY, memberMenuSize]
  );

  async function doToggleChannelNotifications() {
    if (!menuChannelIsText) return;
    if (!selectedMenuChannelDbId) return;

    const nextShow = menuChannelNotificationsEnabled ? 0 : 1;

    setMenuBusy(true);
    setMenuErr('');

    try {
      const res = await comsApiPatch(
        `/text-channels/${encodeURIComponent(selectedMenuChannelDbId)}/notifications`,
        { show_notifications: nextShow }
      );

      const state = safeObj(res && res.state);
      const channelId = String(menuChannel && menuChannel.id != null ? menuChannel.id : '').trim();

      if (channelId) {
        setChannelReadStateById((prev) => ({
          ...prev,
          [channelId]: {
            ...safeObj(prev && prev[channelId]),
            unread_count: Math.max(0, toInt(state.unread_count, safeObj(prev && prev[channelId]).unread_count, 0)),
            mention_count: Math.max(0, toInt(state.mention_count, safeObj(prev && prev[channelId]).mention_count, 0)),
            last_seen_message_id: Math.max(
              0,
              toInt(state.last_seen_message_id, safeObj(prev && prev[channelId]).last_seen_message_id, 0)
            ),
            show_notifications: toInt(state.show_notifications, nextShow) === 1 ? 1 : 0,
          },
        }));
      }

      closeMenu();
    } catch (e) {
      setMenuErr(e?.message || 'Failed to update notifications');
      setMenuBusy(false);
    }
  }

  async function doStartWhatShallWePlay() {
    if (!menuCanStartWhatShallWePlay) return;

    const serverId = String(
      server && (server.id != null ? server.id : server.server_id != null ? server.server_id : '') || ''
    ).trim();
    const channelId = String(menuChannel && menuChannel.id != null ? menuChannel.id : '').trim();
    const channelDbId = String(selectedMenuChannelDbId || '').trim();
    const channelName = String(menuChannel && menuChannel.name ? menuChannel.name : 'Voice').trim() || 'Voice';

    if (!channelDbId) {
      setMenuErr('Could not determine the voice channel id');
      return;
    }

    setMenuBusy(true);
    setMenuErr('');

    try {
      const payload = {
        version: 1,
        type: 'what-shall-we-play',
        sessionId: buildWhatShallWePlaySessionId(),
        serverId,
        channelId,
        channelDbId,
        channelName,
        startedByUserId: myUserId ? String(myUserId) : '',
        startedByName: String(displayName || '').trim() || 'User',
        createdAt: new Date().toISOString(),
        prompt: 'Choose the games you would play.',
        ctaLabel: 'Choose games',
      };

      await comsApiPost(`/voice-channels/${encodeURIComponent(channelDbId)}/messages`, {
        body: buildWhatShallWePlayMessageBody(payload),
      });

      if (typeof onSelectChannel === 'function' && channelId) {
        try {
          onSelectChannel(channelId);
        } catch {}
      }

      if (typeof onStartWhatShallWePlay === 'function') {
        try {
          await Promise.resolve(onStartWhatShallWePlay(payload));
        } catch {}
      } else {
        try {
          window.dispatchEvent(new CustomEvent('vox-what-shall-we-play-start', { detail: payload }));
        } catch {}
      }

      closeMenu();
    } catch (e) {
      setMenuErr(e?.message || 'Failed to start What shall we play?');
      setMenuBusy(false);
    }
  }

  async function doDelete() {
    if (!menuChannel) return;
    if (!apiAvailable) return;
    if (!canManageChannels) return;

    setMenuBusy(true);
    setMenuErr('');

    try {
      const dbId = channelDbIdFromAny(menuChannel);
      if (!dbId) throw new Error('channel_id_missing');

      await comsApiDelete(`/servers/${encodeURIComponent(String(server.id))}/channels/${encodeURIComponent(String(dbId))}`);
      window.dispatchEvent(new CustomEvent('vox-channels-changed'));

      if (toInt(localWelcomeChannelId, 0) > 0 && toInt(dbId, 0) === toInt(localWelcomeChannelId, 0)) {
        setLocalWelcomeChannelId(null);
        setLocalWelcomeEnabled(0);
      }

      closeMenu();

      if (typeof onChannelsChanged === 'function') {
        try {
          await onChannelsChanged();
        } catch {}
      }

      refreshServerWelcomeState().catch(() => {});
    } catch (e) {
      setMenuErr(String((e && e.message) || e));
      setMenuBusy(false);
    }
  }

  function doRename() {
    if (!menuChannel) return;
    if (!apiAvailable) return;
    if (!canManageChannels) return;

    setEditChannelTarget(menuChannel);
    setEditChannelOpen(true);
    closeMenu();
  }

  async function handleEditChannelSaved(payload) {
    const saved = payload && payload.channel ? normalizeChannel(payload.channel) : null;

    if (saved) {
      setRemoteChannels((prev) => {
        const list = safeArray(prev).map(normalizeChannel);
        const idx = list.findIndex((c) => String(c.db_id || c.id || '') === String(saved.db_id || saved.id || ''));
        if (idx === -1) return list;
        const out = list.slice();
        out[idx] = { ...out[idx], ...saved };
        return out;
      });
    }

    await refreshServerWelcomeState().catch(() => {});

    if (typeof onChannelsChanged === 'function') {
      try {
        await onChannelsChanged();
      } catch {}
    }
  }

  function createChannel(kind) {
    if (!canManageChannels) return;
    if (typeof onCreateChannel === 'function') onCreateChannel(kind);
  }

  // Server Settings open for the owner and anyone with a management permission (the sections they see follow
  // their permissions; the server checks every action).
  const canOpenServerSettings = isOwner || (myServerPerms.loaded && ADMIN_PERMISSION_KEYS.some((k) => myServerPerms.has(k)));
  const handleOpenServerSettings = useCallback(() => {
    if (!canOpenServerSettings) return;
    if (typeof onOpenTab === 'function') onOpenTab('server-settings');
  }, [canOpenServerSettings, onOpenTab]);

  const voiceSessionObj = safeObj(voiceSession);
  const voiceStatusValue = String(voiceSessionObj.status || '').trim().toLowerCase();
  const hasVoiceSessionTarget = !!(
    String(voiceSessionObj.serverId || '').trim() &&
    String(voiceSessionObj.channelId || '').trim()
  );
  const showVoiceBar = !!(
    voiceSession &&
    (
      voiceStatusValue === 'joining' ||
      voiceStatusValue === 'connecting' ||
      voiceStatusValue === 'connected' ||
      voiceStatusValue === 'reconnecting' ||
      hasVoiceSessionTarget
    )
  );

  const activeVoiceName = useMemo(() => {
    if (!showVoiceBar) return '';
    try {
      const list = effectiveChannels;
      const ch =
        list.find((c) => String(c.id) === String(voiceSessionObj.channelId)) ||
        list.find((c) => String(channelDbIdFromAny(c) || '') === String(channelDbIdFromAny({ id: voiceSessionObj.channelId }) || ''));
      if (!ch) return 'Voice';
      return String(ch.name || 'Voice');
    } catch {
      return 'Voice';
    }
  }, [showVoiceBar, voiceSessionObj, effectiveChannels]);

  function resolveNameFromServerMembers(p) {
    const uid = extractNumericId(p && (p.userId != null ? p.userId : p.user_id));
    if (!uid) return String((p && p.name) || '').trim();
    const dn = memberByUid.get(uid);
    if (dn) return String(dn).trim();
    return String((p && p.name) || '').trim();
  }

  function voiceMembersForChannel(chId) {
    const dbKey = normalizeDbIdKey(chId);
    const list = safeArray(presenceByChannel[String(dbKey)] || []);
    const connected = [];
    const others = [];

    for (const p of list) {
      if (!p) continue;

      const fixedName = resolveNameFromServerMembers(p) || 'User';
      const uid = extractNumericId(p && (p.userId != null ? p.userId : p.user_id));
      const idForMenu = uid ? String(uid) : String((p && (p.userId || p.user_id)) || fixedName);

      const audioLevelRaw =
        p.audioLevel != null
          ? p.audioLevel
          : p.audio_level != null
            ? p.audio_level
            : p.level != null
              ? p.level
              : 0;

      const audioLevel = Number(audioLevelRaw);

      const voiceAnimation =
        String(
          p.voiceAnimation != null
            ? p.voiceAnimation
            : p.voice_animation != null
              ? p.voice_animation
              : p.active_voice_animation != null
                ? p.active_voice_animation
                : ''
        ).trim() || '';

      const entry = {
        id: idForMenu,
        name: fixedName,
        connected: !!p.connected,
        muted: !!p.muted,
        deafened: !!p.deafened,
        isSpeaking: participantIsSpeaking(p),
        audioLevel: Number.isFinite(audioLevel) ? audioLevel : 0,
        voiceAnimation,
        voice_animation: voiceAnimation,
        active_voice_animation: voiceAnimation,
      };

      if (p.connected) connected.push(entry);
      else others.push(entry);
    }

    return { connected, others };
  }

  async function toggleMemberBlocked() {
    const targetUserId = memberMenuUserId;
    if (!targetUserId || targetUserId === myUserId || memberBlockBusy) return;

    setMemberBlockBusy(true);
    setMemberBlockMsg('');

    try {
      if (memberMenuIsBlocked) {
        await comsApiDelete(`/blocks/${encodeURIComponent(String(targetUserId))}`);
        setMemberBlockMsg('User unblocked');
      } else {
        await comsApiPost(`/blocks/${encodeURIComponent(String(targetUserId))}`, {});
        setMemberBlockMsg('User blocked');
      }

      await refreshBlockedUsers();
    } catch (e) {
      setMemberBlockMsg(String((e && e.message) || e || 'Failed to update block state'));
    } finally {
      setMemberBlockBusy(false);
    }
  }

  function onMemberContextMenu(e, member) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();

    closeMenu();

    const x = (e && Number.isFinite(e.clientX) ? e.clientX : 0) || 0;
    const y = (e && Number.isFinite(e.clientY) ? e.clientY : 0) || 0;

    setMemberMenuOpen(true);
    setMemberMenuX(x);
    setMemberMenuY(y);
    setMemberMenuMember(member);
    setMemberBlockBusy(false);
    setMemberBlockMsg('');

    const prefs = getParticipantPrefs({
      serverId: serverIdForPrefs,
      participantId: String(member.id || ''),
      participantName: String(member.name || ''),
    });

    setMemberMenuPrefs({
      volume: clampInt(prefs.volume, 0, 100),
      muted: !!prefs.muted,
    });
  }

  function setMemberVolume(v) {
    if (!memberMenuMember) return;
    const next = clampInt(v, 0, 100);
    setMemberMenuPrefs((p) => ({ ...p, volume: next }));

    setParticipantPrefs({
      serverId: serverIdForPrefs,
      participantId: String(memberMenuMember.id || ''),
      participantName: String(memberMenuMember.name || ''),
      volume: next,
    });
  }

  function toggleMemberMuted() {
    if (!memberMenuMember) return;
    const nextMuted = !memberMenuPrefs.muted;
    setMemberMenuPrefs((p) => ({ ...p, muted: nextMuted }));

    setParticipantPrefs({
      serverId: serverIdForPrefs,
      participantId: String(memberMenuMember.id || ''),
      participantName: String(memberMenuMember.name || ''),
      muted: nextMuted,
    });
  }

  function renderVoiceMemberRow(m, connected) {
    const isYou = myUserId && extractNumericId(m && m.id) === myUserId;
    const shownName = isYou ? (myMemberName || m.name || displayName) : m.name;
    const voiceRowUid = extractNumericId(m && m.id);
    const rowAvatarUrl = avatarUrlForUserId(voiceRowUid);

    const voiceAnimation =
      String(
        m && m.voiceAnimation != null
          ? m.voiceAnimation
          : m && m.voice_animation != null
            ? m.voice_animation
            : m && m.active_voice_animation != null
              ? m.active_voice_animation
              : ''
      ).trim() || '';

    return {
      key: `${m.id}_${connected ? 'c' : 'o'}`,
      id: m.id,
      connected,
      shownName,
      rowAvatarUrl,
      muted: !!(m && m.muted),
      deafened: !!(m && m.deafened),
      isSpeaking: !!(m && m.isSpeaking),
      audioLevel: Number(m && m.audioLevel) || 0,
      voiceAnimation,
      voice_animation: voiceAnimation,
      active_voice_animation: voiceAnimation,
      member: m,
    };
  }

  function joinVoiceFromChannel(c) {
    if (!c) return;
    const dbId = channelDbIdFromAny(c);
    if (!dbId) return;

    const roomName = buildVoiceRoomName(server, { ...c, db_id: dbId });
    if (typeof onJoinVoiceChannel === 'function') {
      onJoinVoiceChannel(String(server.id), String(c.id), String(dbId), String(roomName), {});
    }
  }

  function handleDrawerSelectServer(sid) {
    const nextId = String(sid || '').trim();
    if (!nextId) return;

    try {
      localStorage.setItem(LAST_COMMS_SERVER_KEY, nextId);
    } catch {}

    if (typeof onSelectServer === 'function') {
      try {
        onSelectServer(nextId);
        return;
      } catch {}
    }

    navigate(`/comms/${encodeURIComponent(nextId)}`);
  }

  function handleDrawerCreateServer(e) {
    if (typeof createServerFn === 'function') {
      createServerFn(e);
    }
  }

  function handleSelectTextChannel(channelId) {
    if (typeof onSelectChannel === 'function') onSelectChannel(channelId);
  }

  function handleSelectVoiceChannel(channelId) {
    if (typeof onSelectChannel === 'function') onSelectChannel(channelId);
  }

  return (
    <div className="coms-sidebar" style={sidebarRootStyle}>
      {hideServerDrawer ? null : (
        <SidebarTopDrawer
          servers={servers}
          activeServerId={serverKey}
          onSelectServer={handleDrawerSelectServer}
          onCreateServer={handleDrawerCreateServer}
          onServersChanged={onServersChanged}
          onOpenServerSettings={onOpenServerSettings}
          apiAvailable={apiAvailable}
          user={user}
          serverUnreadById={serverUnreadById}
          serverMentionCountById={serverMentionCountById}
        />
      )}

      {/* Only when the owner placed the banner here (Server Settings → Server identity). */}
      {serverKey ? <ServerSidebarBanner serverKey={serverKey} compact={!!isMobileViewport} /> : null}

      <SidebarHeader
        server={effectiveServer}
        isMobileViewport={isMobileViewport}
        hasServerLogo={hasServerLogo}
        serverLogoUrl={serverLogoUrl}
        serverAcronym={serverAcronym}
        onLogoError={() => setLogoBroken(true)}
        onOpenOverview={serverKey ? () => setOverviewOpen(true) : null}
      />

      {overviewOpen && serverKey ? (
        <ServerOverviewDialog
          serverKey={serverKey}
          name={effectiveServer && effectiveServer.name ? String(effectiveServer.name) : 'Server'}
          logo={hasServerLogo ? serverLogoUrl : ''}
          onClose={() => setOverviewOpen(false)}
        >
          {effectiveServer && effectiveServer.id ? (
            <div className="vx-sov-extra">
              <ServerEncryptionBadge serverKey={effectiveServer.public_id || effectiveServer.id} />
            </div>
          ) : null}
        </ServerOverviewDialog>
      ) : null}

      <div className="coms-sidebar-search" style={isMobileViewport ? { flexShrink: 0 } : undefined}>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find channels..." />
      </div>

      {serverKey ? (
        <div className="vxo-quicklinks" role="navigation" aria-label="Server tools" style={isMobileViewport ? { flexShrink: 0 } : undefined}>
          <button type="button" className="vxo-quicklink" title="Search (Ctrl+K)" onClick={() => openArea('search', { scope: 'server' })} data-testid="quick-search">
            <span aria-hidden="true">🔎</span> Search
          </button>
          <button type="button" className="vxo-quicklink" title="Files & resources" onClick={() => openArea('library', { tab: 'files' })} data-testid="quick-library">
            <span aria-hidden="true">📁</span> Files
          </button>
          <button type="button" className="vxo-quicklink" title="Bookmarks" onClick={() => openArea('bookmarks')} data-testid="quick-bookmarks">
            <span aria-hidden="true">🔖</span> Saved
          </button>
        </div>
      ) : null}

      <div className="coms-sidebar-scroll" style={sidebarScrollStyle}>
        <SidebarChannelSections
          isOwner={canManageChannels}
          textChannels={activeTextChannels}
          archivedChannels={archivedTextChannels}
          layout={channelLayout}
          isFiltering={!!String(query || '').trim()}
          onSaveLayout={canManageChannels ? onSaveLayout : null}
          onAddLayoutItem={canManageChannels ? onAddLayoutItem : null}
          onOrganise={canManageChannels ? () => setOrgDialog({ kind: 'organise' }) : null}
          voiceChannels={voiceChannels}
          activeChannelId={activeChannelId}
          onSelectChannel={handleSelectTextChannel}
          onChannelContextMenu={onChannelContextMenu}
          onCreateChannel={createChannel}
          isMobileViewport={isMobileViewport}
          joinVoiceFromChannel={joinVoiceFromChannel}
          voiceMembersForChannel={voiceMembersForChannel}
          renderVoiceMemberRow={renderVoiceMemberRow}
          onMemberContextMenu={onMemberContextMenu}
          onSelectVoiceChannel={handleSelectVoiceChannel}
          channelReadStateById={channelReadStateById}
          welcomeChannelId={localWelcomeChannelId}
        />
      </div>

      {menuOpen && menuChannel ? (
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            left: menuPos.x,
            top: menuPos.y,
            width: menuSize.w,
            minHeight: menuSize.h,
            background: 'var(--vxs-1, #0a0f16)',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
            borderRadius: 12,
            boxShadow: '0 18px 40px rgba(0,0,0,0.45)',
            padding: 8,
            zIndex: 2000,
            color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.96 * var(--vx-ov, 1)))',
          }}
        >
          <div
            style={{
              padding: '8px 10px 10px',
              fontSize: 13,
              fontWeight: 800,
              opacity: 0.9,
              borderBottom: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
              marginBottom: 6,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {String(menuChannel && menuChannel.name ? menuChannel.name : 'Channel')}
          </div>

          <button
            type="button"
            onClick={() => {
              const c = menuChannel;
              closeMenu();
              saveBookmark('channel', `${String((c && c.kind) || 'text') === 'voice' ? 'voice' : 'text'}:${channelDbIdFromAny(c)}`);
            }}
            disabled={menuBusy}
            style={{
                width: '100%',
                height: 36,
                border: 0,
                background: 'transparent',
                color: 'inherit',
                textAlign: 'left',
                padding: '0 10px',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            data-testid="channel-menu-bookmark"
          >
            🔖 Save bookmark
          </button>

          {!menuChannelArchived && selectedMenuChannelDbId ? (
            <>
              <button
                type="button"
                onClick={() => setMenuInteractionsOpen((v) => !v)}
                aria-expanded={menuInteractionsOpen}
                aria-haspopup="menu"
                disabled={menuBusy}
                style={{
                  width: '100%',
                  height: 36,
                  border: 0,
                  background: menuInteractionsOpen ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))' : 'transparent',
                  color: 'inherit',
                  textAlign: 'left',
                  padding: '0 10px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                data-testid="channel-menu-interactions"
              >
                <span>📊 Create interaction</span>
                <span aria-hidden="true">{menuInteractionsOpen ? '▾' : '▸'}</span>
              </button>
              {menuInteractionsOpen ? (
                <div style={{ padding: '2px 0 4px 8px' }}>
                  <InteractionMenu
                    serverKey={serverKey}
                    kind={menuChannelIsVoice ? 'voice' : 'text'}
                    channelId={Number(selectedMenuChannelDbId)}
                    channelName={String((menuChannel && menuChannel.name) || '')}
                    onPicked={closeMenu}
                  />
                </div>
              ) : null}
            </>
          ) : null}

          {menuChannelIsText && !menuChannelArchived ? (
            <button
              type="button"
              onClick={() => {
                closeMenu();
                openArea('search', { scope: 'channel' });
              }}
              disabled={menuBusy}
              style={{
                width: '100%',
                height: 36,
                border: 0,
                background: 'transparent',
                color: 'inherit',
                textAlign: 'left',
                padding: '0 10px',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              Search in channel
            </button>
          ) : null}

          {menuCanManageThisChannel && menuChannelIsText ? (
            <button
              type="button"
              onClick={() => (menuChannelArchived ? restoreMenuChannel() : openOrgDialog('archive'))}
              disabled={menuBusy}
              style={{
                width: '100%',
                height: 36,
                border: 0,
                background: 'transparent',
                color: 'inherit',
                textAlign: 'left',
                padding: '0 10px',
                borderRadius: 8,
                cursor: 'pointer',
              }}
              data-testid={menuChannelArchived ? 'channel-menu-restore' : 'channel-menu-archive'}
            >
              {menuChannelArchived ? 'Restore channel' : 'Archive channel…'}
            </button>
          ) : null}

          {menuCanManageThisChannel && menuChannelIsText && !menuChannelArchived ? (
            <button type="button" onClick={() => openOrgDialog('auto')} disabled={menuBusy} style={{
                width: '100%',
                height: 36,
                border: 0,
                background: 'transparent',
                color: 'inherit',
                textAlign: 'left',
                padding: '0 10px',
                borderRadius: 8,
                cursor: 'pointer',
              }}>
              Auto-archive…
            </button>
          ) : null}

          {menuCanManageThisChannel && !menuChannelArchived ? (
            <div style={{ display: 'flex', gap: 4 }}>
              {[
                [-1, 'Move up', 'channel-menu-move-up'],
                [1, 'Move down', 'channel-menu-move-down'],
              ].map(([delta, label, testId]) => (
                <button
                  key={testId}
                  type="button"
                  onClick={() => moveMenuChannel(delta)}
                  disabled={menuBusy || !channelLayout}
                  data-testid={testId}
                  style={{
                    flex: 1,
                    height: 36,
                    border: 0,
                    background: 'transparent',
                    color: 'inherit',
                    textAlign: 'left',
                    padding: '0 10px',
                    borderRadius: 8,
                    cursor: 'pointer',
                  }}
                >
                  {delta < 0 ? '↑' : '↓'} {label}
                </button>
              ))}
            </div>
          ) : null}

          {menuCanManageThisChannel && !menuChannelArchived ? (
            <button type="button" onClick={() => openOrgDialog('move')} disabled={menuBusy} style={{
                width: '100%',
                height: 36,
                border: 0,
                background: 'transparent',
                color: 'inherit',
                textAlign: 'left',
                padding: '0 10px',
                borderRadius: 8,
                cursor: 'pointer',
              }} data-testid="channel-menu-move">
              Move to category…
            </button>
          ) : null}

          {menuCanManageThisChannel && menuChannelIsText ? (
            <button type="button" onClick={() => openOrgDialog('convert')} disabled={menuBusy} style={{
                width: '100%',
                height: 36,
                border: 0,
                background: 'transparent',
                color: 'inherit',
                textAlign: 'left',
                padding: '0 10px',
                borderRadius: 8,
                cursor: 'pointer',
              }}>
              Convert…
            </button>
          ) : null}

          {menuCanManageThisChannel ? (
            <button type="button" onClick={() => openOrgDialog('organise')} disabled={menuBusy} style={{
                width: '100%',
                height: 36,
                border: 0,
                background: 'transparent',
                color: 'inherit',
                textAlign: 'left',
                padding: '0 10px',
                borderRadius: 8,
                cursor: 'pointer',
              }}>
              Organise channels…
            </button>
          ) : null}

          {menuCanToggleNotifications ? (
            <button
              type="button"
              onClick={doToggleChannelNotifications}
              disabled={menuBusy}
              style={{
                width: '100%',
                height: 36,
                border: 0,
                background: 'transparent',
                color: 'inherit',
                textAlign: 'left',
                padding: '0 10px',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              {menuChannelNotificationsEnabled ? 'Disable notifications' : 'Enable notifications'}
            </button>
          ) : null}

          {menuCanStartWhatShallWePlay ? (
            <button
              type="button"
              onClick={doStartWhatShallWePlay}
              disabled={menuBusy}
              style={{
                width: '100%',
                height: 36,
                border: 0,
                background: 'transparent',
                color: 'inherit',
                textAlign: 'left',
                padding: '0 10px',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              What shall we play?
            </button>
          ) : null}

          {menuCanManageThisChannel ? (
            <button
              type="button"
              onClick={doRename}
              disabled={menuBusy}
              style={{
                width: '100%',
                height: 36,
                border: 0,
                background: 'transparent',
                color: 'inherit',
                textAlign: 'left',
                padding: '0 10px',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              Edit Channel
            </button>
          ) : null}

          {menuCanManageThisChannel ? (
            <button
              type="button"
              onClick={doDelete}
              disabled={menuBusy}
              style={{
                width: '100%',
                height: 36,
                border: 0,
                background: 'transparent',
                color: '#ff7875',
                textAlign: 'left',
                padding: '0 10px',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              Delete channel
            </button>
          ) : null}

          {menuErr ? (
            <div
              style={{
                marginTop: 8,
                padding: '0 10px 6px',
                fontSize: 12,
                color: '#ff7875',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            >
              {menuErr}
            </div>
          ) : null}
        </div>
      ) : null}

      {memberMenuOpen && memberMenuMember ? (
        <div
          ref={memberMenuRef}
          style={{
            position: 'fixed',
            left: memberMenuPos.x,
            top: memberMenuPos.y,
            width: memberMenuSize.w,
            minHeight: memberMenuSize.h,
            background: 'var(--vxs-1, #0a0f16)',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
            borderRadius: 12,
            boxShadow: '0 18px 40px rgba(0,0,0,0.45)',
            padding: 10,
            zIndex: 2000,
            color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.96 * var(--vx-ov, 1)))',
          }}
        >
          <div
            style={{
              padding: '4px 2px 10px',
              fontSize: 13,
              fontWeight: 800,
              opacity: 0.9,
              borderBottom: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
              marginBottom: 10,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {String(memberMenuMember && memberMenuMember.name ? memberMenuMember.name : 'Member')}
          </div>

          <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 8 }}>Volume</div>

          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={clampInt(memberMenuPrefs.volume, 0, 100)}
            onChange={(e) => setMemberVolume(e.target.value)}
            style={{ width: '100%', marginBottom: 10 }}
          />

          <button
            type="button"
            onClick={toggleMemberMuted}
            style={{
              width: '100%',
              height: 36,
              border: 0,
              background: 'transparent',
              color: 'inherit',
              textAlign: 'left',
              padding: '0 8px',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            {memberMenuPrefs.muted ? 'Unmute locally' : 'Mute locally'}
          </button>

          {memberMenuUserId > 0 && memberMenuUserId !== myUserId ? (
            <button
              type="button"
              onClick={toggleMemberBlocked}
              disabled={memberBlockBusy}
              style={{
                width: '100%',
                height: 36,
                border: 0,
                background: 'transparent',
                color: '#ff9aa8',
                textAlign: 'left',
                padding: '0 8px',
                borderRadius: 8,
                cursor: memberBlockBusy ? 'default' : 'pointer',
                opacity: memberBlockBusy ? 0.7 : 1,
              }}
            >
              {memberBlockBusy
                ? memberMenuIsBlocked
                  ? 'Unblocking…'
                  : 'Blocking…'
                : memberMenuIsBlocked
                  ? 'Unblock user'
                  : 'Block user'}
            </button>
          ) : null}

          {memberBlockMsg ? (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                opacity: 0.82,
                lineHeight: 1.35,
                color: memberBlockMsg.toLowerCase().includes('failed') ? '#ff9aa8' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.82 * var(--vx-ov, 1)))',
              }}
            >
              {memberBlockMsg}
            </div>
          ) : null}
        </div>
      ) : null}

      {orgDialog && orgDialog.kind === 'archive' ? <ArchiveDialog serverKey={serverKey} channel={orgDialog.channel} onClose={() => setOrgDialog(null)} /> : null}
      {orgDialog && orgDialog.kind === 'auto' ? <AutoArchiveDialog serverKey={serverKey} channel={orgDialog.channel} onClose={() => setOrgDialog(null)} /> : null}
      {orgDialog && orgDialog.kind === 'convert' ? <ConvertDialog serverKey={serverKey} channel={orgDialog.channel} onClose={() => setOrgDialog(null)} /> : null}
      {orgDialog && orgDialog.kind === 'move' ? <MoveToCategoryDialog serverKey={serverKey} channel={orgDialog.channel} categories={categories} layout={channelLayout} onSave={onSaveLayout} onClose={() => setOrgDialog(null)} /> : null}
      {orgDialog && orgDialog.kind === 'organise' ? (
        <ChannelOrganiserDialog
          serverKey={serverKey}
          channels={textChannels}
          onOpenSettings={
            canOpenServerSettings
              ? () => {
                  setOrgDialog(null);
                  try {
                    localStorage.setItem('vx-server-admin-section', 'organisation');
                  } catch {}
                  handleOpenServerSettings();
                  setTimeout(() => window.dispatchEvent(new CustomEvent('vox-open-server-admin', { detail: { section: 'organisation' } })), 0);
                }
              : null
          }
          onClose={() => setOrgDialog(null)}
        />
      ) : null}
      {layoutPromptEl}

      {editChannelOpen && editChannelTarget ? (
        <EditChannelModal
          server={effectiveServer}
          channel={editChannelTarget}
          user={user}
          onClose={closeEditChannel}
          onSaved={handleEditChannelSaved}
        />
      ) : null}

      <div className="coms-sidebar-footer-wrap" style={bottomBarWrapStyle}>
        <SidebarFooter
          showVoiceBar={showVoiceBar}
          myAvatarUrl={myAvatarUrl}
          displayName={displayName}
          voiceSession={voiceSession}
          activeVoiceName={activeVoiceName}
          voiceVideoErr={voiceVideoErr}
          onToggleVoiceMute={onToggleVoiceMute}
          onToggleVoiceDeafen={onToggleVoiceDeafen}
          voiceShowVideo={voiceShowVideo}
          onToggleVoiceVideo={onToggleVoiceVideo}
          isOwner={canOpenServerSettings}
          openServerSettings={handleOpenServerSettings}
          onOpenTab={onOpenTab}
          onLeaveVoiceChannel={onLeaveVoiceChannel}
          onOpenGames={onOpenGames}
          gamesActive={gamesActive}
          activeGame={activeGame}
          serverKey={serverKey || ''}
        />
      </div>
    </div>
  );
}